import React, { useState, useEffect, useContext } from "react";
import {
  Calendar,
  Heart,
  Bell,
  Plus,
  Camera,
  FileText,
  Phone,
  Mail,
  MapPin,
  Clock,
  X,
  Info,
  AlertTriangle,
  Edit,
  Trash2,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import AddPetModal from "./AddPetModal";
import EditPetModal from "./EditPetModal";
import AppointmentModal from "./AppoinmentModel";
import { Link } from "react-router-dom";
import { getPetById, getMedicalHistory } from "../../../services/api";

const PetParentDashboard = () => {
  const {
    loginUser,
    getPets,
    pets,
    petLoading,
    petError,
    getPetAppointment,
    appointments,
    appointmentLoading,
    appointmentError,
  } = useContext(AuthContext);

  // Component state
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedPetMedical, setSelectedPetMedical] = useState(null);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [petToEdit, setPetToEdit] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // User data
  const userData = {
    name: loginUser?.name || "Pet Parent",
    email: loginUser?.email,
  };

  // Mock notifications
  const notifications = [
    {
      id: 1,
      message: "Charlie's vaccination is due in 10 days",
      type: "reminder",
      urgent: true,
      timestamp: "2 minutes ago",
    },
    {
      id: 2,
      message: "New health tips for Golden Retrievers available",
      type: "info",
      urgent: false,
      timestamp: "1 hour ago",
    },
    {
      id: 3,
      message: "Annual wellness packages now available",
      type: "promotion",
      urgent: false,
      timestamp: "3 hours ago",
    },
  ];

  // Effects
  useEffect(() => {
    if (pets === null) {
      getPets();
    }
  }, [getPets, pets]);

  useEffect(() => {
    if (appointments === null) {
      getPetAppointment();
    }
  }, [getPetAppointment, appointments]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedPet || showAddModal || showEditModal || isModalOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0px"; // Prevent scroll jump
    } else {
      document.body.style.overflow = "unset";
      document.body.style.paddingRight = "0px";
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.paddingRight = "0px";
    };
  }, [selectedPet, showAddModal, showEditModal, isModalOpen]);

  const handleBookNow = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPetEmoji = (species) => {
    const emojiMap = {
      dog: "🐕",
      cat: "🐱",
      bird: "🦜",
      rabbit: "🐰",
      hamster: "🐹",
      fish: "🐠",
    };
    return emojiMap[species?.toLowerCase()] || "🐾";
  };

  const getHealthStatus = (pet) => {
    if (!pet.vaccinations || pet.vaccinations.length === 0) {
      return "Needs Attention";
    }

    const hasUpToDateVaccination = pet.vaccinations.some((vacc) => {
      const nextDue = new Date(vacc.nextDue);
      const now = new Date();
      return nextDue > now;
    });

    return hasUpToDateVaccination ? "Good" : "Needs Attention";
  };

  const getHealthStatusColor = (status) => {
    const colorMap = {
      excellent: "bg-green-100 text-green-800",
      good: "bg-blue-100 text-blue-800",
      fair: "bg-yellow-100 text-yellow-800",
      poor: "bg-red-100 text-red-800",
      "needs attention": "bg-orange-100 text-orange-800",
    };
    return colorMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  const getLastVisitDate = (pet) => {
    if (!pet.medicalHistory || pet.medicalHistory.length === 0) {
      return null;
    }
    const sortedHistory = pet.medicalHistory.sort(
      (a, b) => new Date(b.diagnosedDate) - new Date(a.diagnosedDate)
    );
    return sortedHistory[0]?.diagnosedDate;
  };

  const getNextVaccinationDate = (pet) => {
    if (!pet.vaccinations || pet.vaccinations.length === 0) {
      return null;
    }
    const upcomingVaccinations = pet.vaccinations
      .filter((vacc) => new Date(vacc.nextDue) > new Date())
      .sort((a, b) => new Date(a.nextDue) - new Date(b.nextDue));
    return upcomingVaccinations[0]?.nextDue;
  };

  const getDoctorName = (doctor) => {
    if (!doctor) return "Unknown Doctor";
    if (typeof doctor === "string") return doctor;
    if (typeof doctor === "object" && doctor.name) return doctor.name;
    return "Unknown Doctor";
  };

  const getPetName = (pet) => {
    if (!pet) return "Unknown Pet";
    if (typeof pet === "string") return pet;
    if (typeof pet === "object" && pet.name) return pet.name;
    return "Unknown Pet";
  };

  // Enhanced pet details handler - now fetches medical history
  const handleViewDetails = async (pet) => {
    try {
      setActionLoading(true);
      setError("");

      // Fetch pet details
      const petResponse = await getPetById(pet._id);
      if (petResponse.success) {
        setSelectedPet(petResponse.data);

        // Fetch medical history
        try {
          const medicalResponse = await getMedicalHistory(pet._id);
          if (medicalResponse.success) {
            setSelectedPetMedical(medicalResponse.data);
          } else {
            setSelectedPetMedical(null);
          }
        } catch (medicalError) {
          console.error("Failed to fetch medical history:", medicalError);
          setSelectedPetMedical(null);
        }
      } else {
        setError(petResponse.message || "Failed to fetch pet details");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch pet details");
    } finally {
      setActionLoading(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedPet(null);
    setSelectedPetMedical(null);
  };

  // Event handlers
  const handleAddPetSuccess = () => {
    getPets();
    setShowAddModal(false);
  };

  const handleEditPetSuccess = () => {
    getPets();
    setSelectedPet(null);
    setShowEditModal(false);
  };

  const openEditModal = (pet) => {
    setPetToEdit(pet);
    setShowEditModal(true);
    setSelectedPet(null);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setPetToEdit(null);
  };

  const handleAppointmentSuccess = () => {
    console.log("Appointment created successfully!");
  };

  const dismissNotification = (notificationId) => {
    console.log("Dismissing notification:", notificationId);
  };

  // Loading state
  if (petLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  pb-safe mb-10 mt-10 sm:mt-0">
      {/* Main Container with proper padding and spacing */}
      <div className="w-full  mx-auto px-0 sm:px-4 lg:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Header Section - Optimized for mobile */}
        <header className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="space-y-4">
            {/* Title Section */}
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                Welcome back, {userData.name}!
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                Manage your furry family members
              </p>
            </div>

            {/* Action Buttons - Mobile optimized */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="group relative overflow-hidden w-full sm:flex-1 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:from-blue-600 hover:via-blue-700 hover:to-blue-800 text-white px-4 py-3 rounded-xl flex items-center justify-center space-x-3 transition-all duration-300 shadow-lg hover:shadow-xl transform active:scale-95 font-semibold text-sm sm:text-base"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <div className="relative p-1 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors duration-200">
                  <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                </div>
                <span className="relative font-bold">Add Pet</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>

              <Link
                to="/dashboard/create-appointment"
                onClick={handleBookNow}
                className="group relative overflow-hidden w-full sm:flex-1 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 hover:from-purple-600 hover:via-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-xl flex items-center justify-center space-x-3 transition-all duration-300 shadow-lg hover:shadow-xl transform active:scale-95 font-semibold text-sm sm:text-base"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-purple-600 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <div className="relative p-1 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors duration-200">
                  <Calendar className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <span className="relative font-bold">Book Appointment</span>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </Link>
            </div>
          </div>
        </header>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mx-3 sm:mx-0">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={() => setError("")}
                className="text-red-600 hover:text-red-800 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Loading State for actions */}
        {actionLoading && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-xl mx-3 sm:mx-0">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>Processing...</span>
            </div>
          </div>
        )}

        {/* Stats Grid - Mobile optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Pets */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-3 sm:p-4 lg:p-6 border border-blue-200 hover:border-blue-300 group">
            <Link to="/dashboard/my-pets" className="block">
              <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                <div className="p-2 sm:p-3 bg-blue-500 rounded-xl shadow-md group-hover:shadow-lg transition-shadow">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-blue-700 mb-1">
                    Total Pets
                  </p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900">
                    {pets?.length || 0}
                  </p>
                  <div className="hidden sm:block mt-1">
                    <span className="text-xs text-blue-600 font-medium">
                      View all pets →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Appointments */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-3 sm:p-4 lg:p-6 border border-green-200 hover:border-green-300 group">
            <Link to="/dashboard/my-appointment" className="block">
              <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                <div className="p-2 sm:p-3 bg-green-500 rounded-xl shadow-md group-hover:shadow-lg transition-shadow">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-green-700 mb-1">
                    Appointments
                  </p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-900">
                    {appointmentLoading ? (
                      <span className="animate-pulse">...</span>
                    ) : (
                      appointments?.length || 0
                    )}
                  </p>
                  <div className="hidden sm:block mt-1">
                    <span className="text-xs text-green-600 font-medium">
                      Manage appointments →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Notifications */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-3 sm:p-4 lg:p-6 border border-amber-200 hover:border-amber-300 group cursor-pointer">
            <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
              <div className="relative p-2 sm:p-3 bg-amber-500 rounded-xl shadow-md group-hover:shadow-lg transition-shadow">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                {notifications.filter((n) => n.urgent)?.length > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></div>
                )}
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-amber-700 mb-1">
                  Notifications
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-amber-900">
                  {notifications.filter((n) => n.urgent)?.length || 0}
                </p>
                <div className="hidden sm:block mt-1">
                  <span className="text-xs text-amber-600 font-medium">
                    {notifications.filter((n) => n.urgent)?.length > 0
                      ? "Action required"
                      : "All clear"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Health Status */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-3 sm:p-4 lg:p-6 border border-purple-200 hover:border-purple-300 group cursor-pointer">
            <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
              <div className="p-2 sm:p-3 bg-purple-500 rounded-xl shadow-md group-hover:shadow-lg transition-shadow">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-purple-700 mb-1">
                  Health Status
                </p>
                <div className="flex items-center justify-center space-x-2">
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-900">
                    {pets?.filter((pet) => getHealthStatus(pet) === "Good")
                      ?.length || 0}
                  </p>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
                <div className="hidden sm:block mt-1">
                  <span className="text-xs text-purple-600 font-medium">
                    Pets in good health
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid - Mobile optimized */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - My Pets & Appointments */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* My Pets Section */}
            <section className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  My Pets
                </h2>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-colors text-sm active:scale-95"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Pet</span>
                </button>
              </div>

              {pets?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {pets?.map((pet) => (
                    <div
                      key={pet._id}
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer hover:border-blue-200 active:scale-95 touch-manipulation"
                      onClick={() => handleViewDetails(pet)}
                    >
                      <div className="flex items-center space-x-3 sm:space-x-4">
                        <div className="text-3xl sm:text-4xl">
                          {pet.image ? (
                            <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden">
                              <img
                                src={
                                  pet.image ||
                                  `https://via.placeholder.com/150/e5e7eb/9ca3af?text=${
                                    pet.species?.charAt(0) || "P"
                                  }`
                                }
                                alt="Pet"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            getPetEmoji(pet.species)
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {pet.name}
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {pet.breed}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(
                                getHealthStatus(pet)
                              )}`}
                            >
                              {getHealthStatus(pet)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {(() => {
                                if (!pet.dateOfBirth) return "Unknown age";

                                const today = new Date();
                                const birthDate = new Date(pet.dateOfBirth);

                                // Calculate difference in milliseconds
                                const diffMs = today - birthDate;
                                const diffDays = Math.floor(
                                  diffMs / (1000 * 60 * 60 * 24)
                                );
                                const diffMonths =
                                  today.getMonth() -
                                  birthDate.getMonth() +
                                  12 *
                                    (today.getFullYear() -
                                      birthDate.getFullYear());
                                const diffYears =
                                  today.getFullYear() - birthDate.getFullYear();

                                // Adjust years if birthday hasn't occurred yet this year
                                let ageYears = diffYears;
                                if (
                                  today.getMonth() < birthDate.getMonth() ||
                                  (today.getMonth() === birthDate.getMonth() &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  ageYears--;
                                }

                                if (ageYears >= 1) {
                                  return `${ageYears} year${
                                    ageYears > 1 ? "s" : ""
                                  } old`;
                                } else if (diffMonths >= 1) {
                                  return `${diffMonths} month${
                                    diffMonths > 1 ? "s" : ""
                                  } old`;
                                } else {
                                  return `${diffDays} day${
                                    diffDays !== 1 ? "s" : ""
                                  } old`;
                                }
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Mobile-optimized info section */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-500">
                          <div className="truncate">
                            <span className="font-medium">Last visit: </span>
                            {getLastVisitDate(pet)
                              ? formatDate(getLastVisitDate(pet))
                              : "No visits"}
                          </div>
                          <div className="truncate">
                            <span className="font-medium">
                              Next vaccination:{" "}
                            </span>
                            {getNextVaccinationDate(pet)
                              ? formatDate(getNextVaccinationDate(pet))
                              : "Not scheduled"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="text-4xl sm:text-6xl mb-4">🐾</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No pets yet
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">
                    Add your first pet to get started!
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors active:scale-95"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add Your First Pet</span>
                  </button>
                </div>
              )}
            </section>

            {/* Upcoming Appointments Section */}
            <section className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                Upcoming Appointments
              </h2>

              {appointmentLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading appointments...</p>
                </div>
              ) : appointments && appointments?.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {/* Scrollable container with better mobile handling */}
                  <div className="max-h-80 sm:max-h-96 overflow-y-auto -mx-1 px-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {appointments?.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow mb-3 last:mb-0 touch-manipulation"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 capitalize truncate">
                              {appointment.type} -{" "}
                              {getPetName(appointment.pets)}
                            </h3>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span>{formatDate(appointment.date)}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="w-4 h-4 flex-shrink-0" />
                                <span>{appointment.time}</span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 truncate">
                              Dr. {getDoctorName(appointment.doctor)}
                            </p>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${
                              appointment.status === "confirmed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {appointment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No upcoming appointments</p>
                </div>
              )}
            </section>
          </div>

          {/* Right Column - Notifications & Emergency Contact */}
          <div className="space-y-4 sm:space-y-6">
            {/* Notifications Section */}
            <section className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <Bell className="w-5 h-5 mr-2 text-gray-600" />
                  <span className="hidden sm:inline">Notifications</span>
                  <span className="sm:hidden">Alerts</span>
                </h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {notifications?.length}
                </span>
              </div>

              {/* Scrollable notifications with better mobile handling */}
              <div className="space-y-3 max-h-80 overflow-y-auto -mx-1 px-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {notifications?.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 sm:p-4 rounded-xl border-l-4 transition-all duration-200 hover:shadow-md touch-manipulation ${
                      notification.urgent
                        ? "bg-red-50 border-l-red-500 border border-red-200"
                        : "bg-blue-50 border-l-blue-500 border border-blue-200"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          {notification.urgent ? (
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          ) : (
                            <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          )}
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded-full ${
                              notification.urgent
                                ? "bg-red-100 text-red-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {notification.type}
                          </span>
                        </div>
                        <p
                          className={`text-sm font-medium leading-relaxed ${
                            notification.urgent
                              ? "text-red-800"
                              : "text-blue-800"
                          }`}
                        >
                          {notification.message}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className="text-xs text-gray-500 flex items-center">
                            <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                            {notification.timestamp}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => dismissNotification(notification.id)}
                        className={`p-1 rounded-full hover:bg-gray-100 transition-colors active:scale-95 ${
                          notification.urgent
                            ? "text-red-400 hover:text-red-600"
                            : "text-blue-400 hover:text-blue-600"
                        }`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Emergency Contact Section */}
            <section className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                Emergency Contact
              </h2>

              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-xl border-l-4 border-red-500 touch-manipulation">
                  <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-red-700 block">
                      Emergency Hotline
                    </span>
                    <p className="text-base font-semibold text-red-800">
                      (555) 911-PETS
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl touch-manipulation">
                  <Mail className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-600 block">
                      Emergency Email
                    </span>
                    <p className="text-sm text-gray-700 break-all">
                      connect@innovet.in
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl touch-manipulation">
                  <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-600 block">
                      Location
                    </span>
                    <p className="text-sm text-gray-700">
                      24/7 Emergency Clinic
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Enhanced Pet Details Modal - Now includes complete medical information */}
        {selectedPet && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
              {/* Modal Header - Fixed */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-white">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  Pet Details - {selectedPet.name}
                </h2>
                <button
                  onClick={closeDetailModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100 active:scale-95"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                  {/* Left Column - Basic Information */}
                  <div>
                    <div className="text-center mb-6">
                      <div className="text-6xl sm:text-8xl mb-4">
                        {selectedPet.image ? (
                          <div className="w-32 h-32 bg-gray-100 rounded-full overflow-hidden mx-auto">
                            <img
                              src={
                                selectedPet.image ||
                                `https://via.placeholder.com/150/e5e7eb/9ca3af?text=${
                                  selectedPet.species?.charAt(0) || "P"
                                }`
                              }
                              alt="Pet"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="text-8xl">
                            {getPetEmoji(selectedPet.species)}
                          </div>
                        )}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {selectedPet.name}
                      </h3>
                      <p className="text-gray-600">{selectedPet.breed}</p>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Basic Information
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Species:</span>
                            <span className="text-gray-900">
                              {selectedPet.species}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Breed:</span>
                            <span className="text-gray-900">
                              {selectedPet.breed}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age:</span>
                            <span className="text-gray-900">
                              {(() => {
                                if (!selectedPet.dateOfBirth) return "Unknown";

                                const today = new Date();
                                const birthDate = new Date(
                                  selectedPet.dateOfBirth
                                );

                                // Calculate difference in milliseconds
                                const diffMs = today - birthDate;
                                const diffDays = Math.floor(
                                  diffMs / (1000 * 60 * 60 * 24)
                                );
                                const diffMonths =
                                  today.getMonth() -
                                  birthDate.getMonth() +
                                  12 *
                                    (today.getFullYear() -
                                      birthDate.getFullYear());
                                const diffYears =
                                  today.getFullYear() - birthDate.getFullYear();

                                // Adjust years if birthday hasn't occurred yet this year
                                let ageYears = diffYears;
                                if (
                                  today.getMonth() < birthDate.getMonth() ||
                                  (today.getMonth() === birthDate.getMonth() &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  ageYears--;
                                }

                                if (ageYears >= 1) {
                                  return `${ageYears} year${
                                    ageYears > 1 ? "s" : ""
                                  }`;
                                } else if (diffMonths >= 1) {
                                  return `${diffMonths} month${
                                    diffMonths > 1 ? "s" : ""
                                  }`;
                                } else {
                                  return `${diffDays} day${
                                    diffDays !== 1 ? "s" : ""
                                  }`;
                                }
                              })()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Weight:</span>
                            <span className="text-gray-900">
                              {selectedPet.weight}kg
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Gender:</span>
                            <span className="text-gray-900 capitalize">
                              {selectedPet.gender}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Color:</span>
                            <span className="text-gray-900">
                              {selectedPet.color}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Medical Information */}
                  <div>
                    <div className="space-y-6">
                      {/* Allergies */}
                      {selectedPetMedical?.allergies && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-3">
                            Allergies & Medical History
                          </h4>
                          <p className="text-gray-700 text-sm">
                            {selectedPetMedical.allergies}
                          </p>
                        </div>
                      )}

                      {/* Vaccinations */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Vaccinations
                        </h4>
                        {selectedPetMedical?.vaccinations &&
                        selectedPetMedical.vaccinations.length > 0 ? (
                          <div className="space-y-3">
                            {selectedPetMedical.vaccinations
                              .slice(0, 5)
                              .map((vaccination, index) => (
                                <div
                                  key={index}
                                  className="bg-white p-3 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    {vaccination.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Date: {formatDate(vaccination.date)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">
                            No vaccination records
                          </p>
                        )}
                      </div>

                      {/* Dewormings */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Deworming History
                        </h4>
                        {selectedPetMedical?.dewormings &&
                        selectedPetMedical.dewormings.length > 0 ? (
                          <div className="space-y-3">
                            {selectedPetMedical.dewormings
                              .slice(0, 5)
                              .map((deworming, index) => (
                                <div
                                  key={index}
                                  className="bg-white p-3 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    {deworming.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Date: {formatDate(deworming.date)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No deworming records</p>
                        )}
                      </div>

                      {/* Spayed/Neutered */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Spay/Neuter Status
                        </h4>
                        {selectedPetMedical?.spayedNeutered &&
                        selectedPetMedical.spayedNeutered.length > 0 ? (
                          <div className="space-y-3">
                            {selectedPetMedical.spayedNeutered.map(
                              (entry, index) => (
                                <div
                                  key={index}
                                  className="bg-white p-3 rounded border"
                                >
                                  <div className="font-medium text-gray-900">
                                    Status:{" "}
                                    {entry.status === "yes"
                                      ? "Spayed/Neutered"
                                      : "Not Spayed/Neutered"}
                                  </div>
                                  {entry.status === "yes" && entry.date && (
                                    <div className="text-sm text-gray-600">
                                      Date: {formatDate(entry.date)}
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500">
                            No spay/neuter information
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer - Fixed */}
              <div className="p-4 sm:p-6 border-t border-gray-200 bg-white">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      closeDetailModal();
                      openEditModal(selectedPet);
                    }}
                    disabled={actionLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl flex items-center justify-center space-x-2 transition-colors font-medium active:scale-95 disabled:opacity-50"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit Pet</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <AddPetModal
          isOpen={showAddModal}
          onClose={closeModals}
          onSuccess={handleAddPetSuccess}
        />

        <EditPetModal
          isOpen={showEditModal}
          onClose={closeModals}
          onSuccess={handleEditPetSuccess}
          pet={petToEdit}
        />
      </div>

      {/* Custom CSS for better scrolling */}
      <style jsx>{`
        /* Smooth scrolling for all scrollable elements */
        * {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }

        /* Better scrollbar styling */
        .scrollbar-thin {
          scrollbar-width: thin;
        }

        .scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 0.5rem;
        }

        .scrollbar-track-gray-100::-webkit-scrollbar-track {
          background-color: #f3f4f6;
          border-radius: 0.5rem;
        }

        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        /* Touch optimization */
        .touch-manipulation {
          touch-action: manipulation;
        }

        /* Prevent horizontal scroll */
        body {
          overflow-x: hidden;
        }

        /* Safe area padding for mobile devices */
        .pb-safe {
          padding-bottom: env(safe-area-inset-bottom);
        }

        /* Improve tap targets for mobile */
        @media (max-width: 640px) {
          button,
          [role="button"],
          .cursor-pointer {
            min-height: 44px;
            min-width: 44px;
          }
        }

        /* Prevent text selection on interactive elements */
        button,
        [role="button"],
        .cursor-pointer {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }

        /* Better focus states for accessibility */
        button:focus-visible,
        [role="button"]:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Smooth transitions for better UX */
        .transition-all {
          transition-property: all;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }

        /* Hide scrollbar while maintaining functionality */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default PetParentDashboard;
